/**
 * @module builder
 */
import type { OptionsDocument, QuoteDocument, TieredPackages, TieredPackageTierId } from 'paintscout'
import { calculateQuote } from '../util'
import { applyTierProducts } from './apply-tier-products'
import { canResolveTierProducts } from './swap-tier-products'
import { TIER_ORDER } from './default-tiered-packages'
import { getTierPrices } from './get-tier-prices'
import { getTierPricingSubTotal } from './resolve-tier-prices'
import type { TierPriceInfo } from './get-tier-prices'

/**
 * Price every tier as if it were selected, and stamp the result on each tier.
 *
 * Only meaningful once a package set has product rules: a swapped tier's price comes out of a real
 * recalculation, not from its percentage adjustment, and the customer view cannot run that
 * calculation itself because it never receives the product library (see filterOptionsDetails). So
 * the estimator resolves all three prices here, while it still has the full options document.
 *
 * Costs one recalculation per tier. Sets nothing when there are no rules, which keeps every
 * existing quote on the old pure-math path in getTierPrices.
 *
 * Exported because the Packages drawer needs the same numbers before anything is saved: picking a
 * template with product rules has to show what those rules do to this estimate.
 */
export function withResolvedTierPrices(args: {
  quote: QuoteDocument
  options: OptionsDocument
  tieredPackages: TieredPackages
}): TieredPackages {
  const { quote, options, tieredPackages } = args

  if (!tieredPackages.tiers || !canResolveTierProducts({ tieredPackages, options })) {
    return tieredPackages
  }

  // Price each tier from the base estimate, not from whatever tier the quote currently sits on.
  const baseQuote = applyTierProducts({
    quote: { ...quote, tieredPackages },
    options,
    fromTierId: tieredPackages.selectedTierId,
    toTierId: tieredPackages.baseTierId
  })

  const tiers = TIER_ORDER.reduce(
    (acc, tierId) => {
      const tier = tieredPackages.tiers[tierId]
      if (!tier) {
        return acc
      }

      const tierQuote = applyTierProducts({
        quote: baseQuote,
        options,
        fromTierId: tieredPackages.baseTierId,
        toTierId: tierId
      })

      // The sub total after the manual adjustment, same basis as every other tier price (ENG-2107).
      acc[tierId] = { ...tier, resolvedPrice: tierQuote.totals ? getTierPricingSubTotal(tierQuote.totals) : undefined }

      return acc
    },
    { ...tieredPackages.tiers }
  )

  return { ...tieredPackages, tiers }
}

/**
 * Tier prices for display, from a quote whose stamps may be stale.
 *
 * `resolvedPrice` is written only when a template is applied or a tier is switched, so on a set with
 * product rules it describes the estimate as it stood at that moment — $0 when the template landed
 * on an estimate with nothing in it yet. Re-stamping before pricing means a surface shows what the
 * packages cost now instead of what they cost then, which is the whole of ENG-1795: the prices used
 * to appear only once the estimator switched packages, because switching was what re-stamped them.
 *
 * Every surface that displays tier prices should read them through here rather than calling
 * getTierPrices directly — the sidebar did this by hand while the Total card's dropdown and the
 * Compare dialog did not, which is why the same bug came back on the surfaces nobody had patched.
 *
 * A no-op in the customer view: the cut-down product library leaves the swap unresolvable, so
 * withResolvedTierPrices returns the config untouched and the estimator's stamped prices stand.
 */
export function getResolvedTierPrices(args: {
  quote: QuoteDocument
  options: OptionsDocument
}): Record<TieredPackageTierId, TierPriceInfo> | null {
  const { quote, options } = args
  const tieredPackages = quote.tieredPackages

  if (!tieredPackages) {
    return null
  }

  const resolved = withResolvedTierPrices({ quote, options, tieredPackages })

  return getTierPrices({ quote: { ...quote, tieredPackages: resolved } })
}

/**
 * Write the full tiered-packages config onto the quote and recalculate totals
 * (so the selected tier's delta flows into the total table).
 *
 * When the incoming config selects a different tier than the quote currently sits on, the products
 * move with it. That covers turning the feature off, which selects the base tier and so must put
 * the base products back.
 */
export function setTieredPackages(args: {
  quote: QuoteDocument
  options: OptionsDocument
  tieredPackages: TieredPackages
  calculate?: boolean
}): QuoteDocument {
  const { quote, options, calculate = true } = args

  // Swap using the rules the quote is about to have — an apply-a-template call carries brand-new
  // rules, and the old config may have had none at all.
  const previousTierId = quote.tieredPackages?.selectedTierId ?? args.tieredPackages.selectedTierId
  const swappedQuote = applyTierProducts({
    quote: { ...quote, tieredPackages: args.tieredPackages },
    options,
    fromTierId: previousTierId,
    toTierId: args.tieredPackages.selectedTierId,
    calculate
  })

  const tieredPackages = withResolvedTierPrices({
    quote: swappedQuote,
    options,
    tieredPackages: args.tieredPackages
  })

  const updatedQuote: QuoteDocument = {
    ...swappedQuote,
    tieredPackages
  }

  return calculate ? calculateQuote({ quote: updatedQuote, options }) : updatedQuote
}

/**
 * Switch the active tier (which drives the quote total) and recalculate.
 *
 * Applies the tier's product substitutions on the way, so every caller — the estimator sidebar, the
 * customer-facing gate, the compare dialog, the inline selector — swaps products for free.
 */
export function setSelectedTier(args: {
  quote: QuoteDocument
  options: OptionsDocument
  tierId: TieredPackageTierId
  calculate?: boolean
}): QuoteDocument {
  const { quote, options, tierId, calculate = true } = args

  if (!quote.tieredPackages) {
    return quote
  }

  const swappedQuote = applyTierProducts({
    quote,
    options,
    fromTierId: quote.tieredPackages.selectedTierId,
    toTierId: tierId,
    calculate
  })

  const tieredPackages = withResolvedTierPrices({
    quote: swappedQuote,
    options,
    // Re-stamp from the config as it will be, so the prices match the tier now selected. In the
    // customer view this is a no-op: the product library there is partial, so the estimator's
    // stamped prices survive.
    tieredPackages: { ...swappedQuote.tieredPackages, selectedTierId: tierId }
  })

  const updatedQuote: QuoteDocument = {
    ...swappedQuote,
    tieredPackages
  }

  return calculate ? calculateQuote({ quote: updatedQuote, options }) : updatedQuote
}
